import React from 'react';

import alertIcon from '@assets/img/icons/alert.svg';
import batteryIcon from '@assets/img/icons/battery.svg';
import cosmeticsIcon from '@assets/img/icons/cosmetics.svg';
import dangerousIcon from '@assets/img/icons/dangerous.svg';
import foodIcon from '@assets/img/icons/food.svg';
import intellectualIcon from '@assets/img/icons/intellectual.svg';
import kitchenwareIcon from '@assets/img/icons/kitchenware.svg';
import livingThingsIcon from '@assets/img/icons/living-things.svg';
import pharmaceuticalIcon from '@assets/img/icons/pharmaceutical.svg';
import tablewareIcon from '@assets/img/icons/tableware.svg';
import toysIcon from '@assets/img/icons/toys.svg';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { bookingCautionsStrings } from '@data/bookingInquiry';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';

const strings = createStrings({
  ja: {
    cautionsTitle: '注意事項',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialog: {
    maxWidth: '960px !important',
  },
  alertIconTitle: {
    marginRight: 10,
  },
  cautionsDialogContent: {
    ...common.flexColumn,
    rowGap: 20,
    paddingTop: 20,
    paddingLeft: 80,
    paddingRight: 80,
    paddingBottom: 50,
    [theme.breakpoints.down('sm')]: {
      padding: '1rem',
    },
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      width: '100%',
    },
  },
  alertBanner: {
    ...common.normalText,
    ...common.fontWeight800,
    color: theme.palette.common.white,
    backgroundColor: '#F05F66',
    textAlign: 'center',
    letterSpacing: 5.5,
    lineHeight: '36px',
  },
  importantCargos: {
    ...common.flexInline,
    borderRadius: 22,
    border: '4px solid #00647D',
    boxShadow: '0px 2px 8px rgba(200, 201, 204, 0.5)',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
      alignItems: 'stretch',
    },
  },
  importantCargosTitle: {
    ...common.flexCenter,
    ...common.normalText,
    ...common.fontWeight800,
    color: theme.palette.common.white,
    width: 125,
    backgroundColor: theme.palette.primary.main,
    paddingLeft: 20,
    paddingRight: 20,
    [theme.breakpoints.down('xs')]: {
      paddingTop: 20,
      paddingBottom: 20,
    },
  },
  importantCargosContent: {
    ...common.flexInline,
    ...common.justifyBetween,
    rowGap: 20,
    flexWrap: 'wrap',
    flex: 1,
    paddingLeft: 30,
    paddingRight: 30,
    paddingTop: 20,
    paddingBottom: 20,
  },
  cargoItem: {
    ...common.flexColumnCenter,
    ...common.exSmallerText,
    color: theme.palette.common.black,
    width: 100,
  },
  inputNotesPrompt: {
    ...common.normalText,
    ...common.fontWeight800,
    color: theme.palette.common.black,
    lineHeight: 2.5,
  },
  noteItem: {
    ...common.smallerText,
    color: theme.palette.grey[800],
    lineHeight: 1.25,
    marginBottom: '0.5rem',
    position: 'relative',
    '&::before': {
      content: '""',
      height: 5,
      width: 5,
      position: 'absolute',
      top: 5,
      left: -15,
      backgroundColor: theme.palette.primary.main,
    },
  },
  noteItemContent: {
    ...common.exSmallerText,
    color: theme.palette.grey[700],
    marginTop: '0.25rem',
  },
  inputNotes: {
    columnCount: 2,
    columnGap: '1rem',
    height: 180,
    [theme.breakpoints.down('sm')]: {
      columnCount: 1,
      height: 'auto',
      padding: '0 1rem',
    },
  },
}));

interface CargoItemProps {
  image: string;
  title: React.ReactNode;
}

const CargoItem: React.FC<CargoItemProps> = (props) => {
  const { image, title } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.cargoItem}>
      <img src={image} alt="Cargo Item Icon" />
      <span style={{ whiteSpace: 'nowrap', lineHeight: 3 }}>{title}</span>
    </div>
  );
};

interface NoteItemProps {
  title: string;
  content?: string;
}

const NoteItem: React.FC<NoteItemProps> = (props) => {
  const { title, content } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.noteItem}>
      {title}
      {content && <div className={classes.noteItemContent}>{content}</div>}
    </div>
  );
};

export interface CautionsDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CautionsDialog: React.FC<CautionsDialogProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderImportantCargos = () => (
    <div className={classes.importantCargos}>
      <div className={classes.importantCargosTitle}>
        {bookingCautionsStrings.importantCargosTitle}
      </div>
      <div className={classes.importantCargosContent}>
        <CargoItem image={cosmeticsIcon} title={bookingCautionsStrings.cosmetics} />
        <CargoItem image={batteryIcon} title={bookingCautionsStrings.battery} />
        <CargoItem image={dangerousIcon} title={bookingCautionsStrings.dangerous} />
        <CargoItem image={pharmaceuticalIcon} title={bookingCautionsStrings.pharmaceutical} />
        <CargoItem image={tablewareIcon} title={bookingCautionsStrings.tableware} />
        <CargoItem image={kitchenwareIcon} title={bookingCautionsStrings.kitchenware} />
        <CargoItem image={foodIcon} title={bookingCautionsStrings.food} />
        <CargoItem image={livingThingsIcon} title={bookingCautionsStrings.livingThings} />
        <CargoItem image={toysIcon} title={bookingCautionsStrings.toys()} />
        <CargoItem image={intellectualIcon} title={bookingCautionsStrings.intellectural} />
      </div>
    </div>
  );

  return (
    <ActionableDialog
      {...props}
      classes={{
        paper: classes.dialog,
      }}
    >
      <ActionableDialogTitle>
        <img src={alertIcon} alt="info" className={classes.alertIconTitle} />
        {strings.cautionsTitle}
      </ActionableDialogTitle>
      <ActionableDialogContent>
        <div className={classes.cautionsDialogContent}>
          <div className={classes.alertBanner}>{bookingCautionsStrings.alertBanner}</div>

          {renderImportantCargos()}

          <div className={classes.inputNotesPrompt}>
            {bookingCautionsStrings.inputNotesPrompt()}
          </div>

          <div className={classes.inputNotes}>
            {bookingCautionsStrings.notes().map(([title, content]) => (
              <NoteItem key={title} title={title} content={content} />
            ))}
          </div>
        </div>
      </ActionableDialogContent>
    </ActionableDialog>
  );
};
